import React from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  List,
  ListItem, // <-- Changed from ListItemButton
  ListItemText,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Alert,
  ListItemButton,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { Footer, Header, CustomSnackbar } from "../../shared"; 
import { rolesAdmin, useSecurityCheck } from "../../../utils"; 
import { motion } from "framer-motion";
import { useStepManager } from "utils/hooks/useStepManager"; 
import { StepDialog } from "features/info/StepDialog"; 
import { useUtility } from "utils/context/UtilityContext"; 

export const StepManager = () => {
  const securityCheck = useSecurityCheck(rolesAdmin);
  const { marginBottomHeader } = useUtility();
  const {
    steps,
    loading,
    isSaving,
    openDialog,
    currentStep,
    expandedStepId,
    snackbar,
    handleSaveStep,
    handleDeleteStep,
    handleExpandStep,
    handleSnackbarClose,
    handleOpenAddDialog,
    handleOpenEditDialog,
    handleCloseDialog,
  } = useStepManager();

  if (securityCheck) {
    return securityCheck;
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (steps.length === 0) {
      return (
        <Alert severity="info">
          Aucune étape trouvée. Commencez par en ajouter une.
        </Alert>
      );
    }

    return (
      <Paper elevation={1}>
        <List>
          {steps.map((step) => (
            <React.Fragment key={step.id}>
              {/* FIX: Use <ListItem> as the container and put icons in `secondaryAction`.
                This prevents the icons from stacking or overlapping.
              */}
              <ListItem
                divider
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleOpenEditDialog(step)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteStep(step.id!)}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="expand"
                      onClick={() => handleExpandStep(step.id!)}
                    >
                      {expandedStepId === step.id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                }
              >
                {/* This button now correctly fills the space left of the actions */}
                <ListItemButton 
                  onClick={() => handleExpandStep(step.id!)}
                  sx={{ pr: '120px' }} // Add padding to avoid text overlapping icons
                >
                  <ListItemText primary={step.step} />
                </ListItemButton>
              </ListItem>
              <Collapse
                in={expandedStepId === step.id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    p: 2,
                    pl: 4,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Typography variant="body2">{step.description}</Typography>
                </Box>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      className={`flex flex-col min-h-screen ${marginBottomHeader}`} // This className is redundant but kept as is
    >
      <Header />

      <Container maxWidth="md" sx={{ flexGrow: 1, py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h4" component="h1">
              Gestion des Étapes
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Ajouter une étape
            </Button>
          </Box>

          {renderContent()}
        </Stack>
      </Container>

      <Footer />

      <StepDialog
        open={openDialog}
        isSaving={isSaving}
        onClose={handleCloseDialog}
        onSave={handleSaveStep}
        initialData={currentStep}
      />
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        isSuccess={snackbar.severity === "success"}
        handleClose={handleSnackbarClose}
      />
    </motion.div>
  );
};